import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { Input, ConfigProvider } from 'antd';
import type { InputProps, InputRef } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'react-day-picker/dist/style.css';
import { AccessiblePopover } from '../AccessiblePopover/AccessiblePopover';
import { enUS, fr, nl, Locale } from 'date-fns/locale';
import { useInitialRender } from '../../utils';

const matchDateFnLocale: { [key: string]: Locale } = {
  en: enUS,
  fr: fr,
  nl_BE: nl,
  nl_NL: nl,
  nl: nl,
};

export interface AccessibleDatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  format?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  required?: boolean;
  error?: boolean;
  showMonthYearSelect?: boolean;
  primaryColor?: string;
  inputProps?: InputProps;
}

export const AccessibleDatePicker = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  format: dateFormat = 'DD/MM/YYYY',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  required = false,
  error = false,
  showMonthYearSelect = false,
  primaryColor,
  inputProps,
}: AccessibleDatePickerProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dateFnLocale = matchDateFnLocale[language] ?? enUS;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<InputRef>(null);
  const wasOpenRef = useRef(isOpen);

  const isInitialRender = useInitialRender();

  // Update internal state when value prop changes
  useEffect(() => {
    setSelectedDate(value);
    if (value) {
      setInputValue(dayjs(value).format(dateFormat));
    } else {
      setInputValue('');
    }
  }, [value, dateFormat]);

  // Handle date selection from calendar
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setIsOpen(false);

    if (date) {
      setInputValue(dayjs(date).format(dateFormat));
    } else {
      setInputValue('');
    }

    onChange?.(date);
  };

  // Handle manual input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Try to parse the input as a date
    // This is a simple implementation - in production you might want more robust date parsing
    if (newValue.trim() === '') {
      setSelectedDate(undefined);
      onChange?.(undefined);
    }
  };

  // Handle input blur - validate and parse date
  const handleInputBlur = () => {
    if (inputValue.trim() === '') {
      setSelectedDate(undefined);
      onChange?.(undefined);
      return;
    }

    // Simple date parsing - you might want to use a more robust library
    const parsedDate = dayjs(inputValue, dateFormat);
    if (parsedDate.isValid()) {
      const dateObj = parsedDate.toDate();
      setSelectedDate(dateObj);
      setInputValue(parsedDate.format(dateFormat));
      onChange?.(dateObj);
    } else {
      // Invalid date - revert to previous value
      if (selectedDate) {
        setInputValue(dayjs(selectedDate).format(dateFormat));
      } else {
        setInputValue('');
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (isInitialRender || isOpen || !wasOpen) {
      return;
    }

    setTimeout(() => {
      const inputElement = inputRef.current;
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }, 150);
  }, [isInitialRender, isOpen]);

  const displayPlaceholder =
    placeholder || t('common.selectDate', 'Select date');

  return (
    <ConfigProvider>
      <AccessiblePopover.Root open={isOpen} onOpenChange={setIsOpen}>
        <AccessiblePopover.Trigger asChild>
          <div>
            <Input
              ref={inputRef}
              tabIndex={0}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={displayPlaceholder}
              disabled={disabled}
              suffix={<CalendarOutlined aria-hidden="true" />}
              onClick={() => !disabled && setIsOpen(!isOpen)}
              aria-label={ariaLabel || t('common.datePicker', 'Date picker')}
              aria-describedby={ariaDescribedBy}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              role="combobox"
              aria-required={required}
              aria-invalid={error}
              className={error ? 'border-red-500' : ''}
              {...inputProps}
            />
          </div>
        </AccessiblePopover.Trigger>
        <AccessiblePopover.Content
          align="start"
          side="bottom"
          className="p-0"
          aria-label={t('common.calendar', 'Calendar')}
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            defaultMonth={selectedDate || new Date()}
            showOutsideDays
            fixedWeeks
            locale={dateFnLocale}
            captionLayout={showMonthYearSelect ? 'dropdown' : 'label'}
            style={
              {
                '--rdp-accent-color': primaryColor,
              } as React.CSSProperties
            }
          />
        </AccessiblePopover.Content>
      </AccessiblePopover.Root>
    </ConfigProvider>
  );
};
